<template>
  <div class="log-viewer">
    <!-- Log Container -->
    <div ref="logContainer" class="log-container" @scroll="onScroll">
      <!-- Logs -->
      <div
        v-for="(log, index) in allLogs"
        :key="log.id || index"
        class="log-entry"
      >
        <div class="log-line">
          <span class="log-timestamp">{{ formatTimestamp(log) }}</span>
          <span class="log-level" :class="'level-' + getLevel(log).toLowerCase()">
            [{{ getLevel(log) }}]
          </span>
          <span class="log-message">{{ log.message }}</span>
          <button
            v-if="hasContext(log)"
            class="context-toggle"
            @click="toggleContext(log.id || index)"
          >
            <v-icon x-small>{{ isContextExpanded(log.id || index) ? 'mdi-chevron-down' : 'mdi-chevron-right' }}</v-icon>
            context
          </button>
        </div>
        <div
          v-if="hasContext(log) && isContextExpanded(log.id || index)"
          class="log-context-block"
        >
          <pre class="log-context-json" v-html="formatContextHighlighted(log)"></pre>
        </div>
      </div>

      <!-- Empty state -->
      <div v-if="!isLoadingInitial && allLogs.length === 0" class="empty-state">
        Aucun log disponible
      </div>
    </div>

    <!-- Loading indicator (top right) -->
    <div v-if="isLoadingInitial || isLoadingOlder" class="loading-indicator">
      <v-progress-circular indeterminate size="16" width="2" color="white" />
    </div>

    <!-- Scroll to bottom button -->
    <v-btn
      v-if="!isAtBottom && allLogs.length > 0"
      fab
      x-small
      color="primary"
      class="scroll-to-bottom-btn"
      @click="scrollToBottomAndFollow"
    >
      <v-icon small>mdi-chevron-down</v-icon>
    </v-btn>

    <!-- Live indicator -->
    <div v-if="isStreaming" class="live-indicator">
      <span class="live-dot"></span>
      Live
    </div>
  </div>
</template>

<script>
export default {
  name: 'LogViewer',
  props: {
    executionId: { type: String, required: true },
  },
  data() {
    return {
      // Logs storage
      allLogs: [],
      seenIds: new Set(),

      // Pagination cursors
      newestLog: null,
      oldestLog: null,

      // Loading states
      isLoadingInitial: false,
      isLoadingOlder: false,
      hasMoreOlder: true,

      // SSE
      eventSource: null,
      isStreaming: false,

      // Scroll state
      isAtBottom: true,
      followMode: true,
      scrollThreshold: 400, // pixels from top to trigger load
      bottomThreshold: 50,  // pixels from bottom to consider "at bottom"

      // Context expansion state
      expandedContexts: new Set(),
    };
  },

  mounted() {
    this.loadInitialLogs();
  },

  beforeDestroy() {
    this.stopStreaming();
  },

  methods: {
    // ==================== FORMATTING ====================

    formatTimestamp(log) {
      const ts = log.date || log.timestamp || '';
      if (!ts) return '';
      return ts.includes('T') ? ts.replace('T', ' ').substring(0, 19) : ts;
    },

    getLevel(log) {
      return (log.levelName || log.level || 'INFO').toString().toUpperCase();
    },

    hasContext(log) {
      return log.context && typeof log.context === 'object' && Object.keys(log.context).length > 0;
    },

    formatContext(log) {
      try {
        return JSON.stringify(log.context);
      } catch {
        return '';
      }
    },

    formatContextPretty(log) {
      try {
        return JSON.stringify(log.context, null, 2);
      } catch {
        return '';
      }
    },

    formatContextHighlighted(log) {
      try {
        const json = JSON.stringify(log.context, null, 2);
        // Escape HTML first
        const escaped = json
          .replace(/&/g, '&amp;')
          .replace(/</g, '&lt;')
          .replace(/>/g, '&gt;');
        // Apply syntax highlighting
        return escaped
          // Strings (but not keys)
          .replace(/: "(.*?)"/g, ': <span class="json-string">"$1"</span>')
          // Keys
          .replace(/"([^"]+)":/g, '<span class="json-key">"$1"</span>:')
          // Numbers
          .replace(/: (-?\d+\.?\d*)/g, ': <span class="json-number">$1</span>')
          // Booleans
          .replace(/: (true|false)/g, ': <span class="json-boolean">$1</span>')
          // Null
          .replace(/: (null)/g, ': <span class="json-null">$1</span>');
      } catch {
        return '';
      }
    },

    toggleContext(logId) {
      if (this.expandedContexts.has(logId)) {
        this.expandedContexts.delete(logId);
      } else {
        this.expandedContexts.add(logId);
      }
      // Force reactivity
      this.expandedContexts = new Set(this.expandedContexts);
    },

    isContextExpanded(logId) {
      return this.expandedContexts.has(logId);
    },

    getLogKey(log) {
      if (log.id) return String(log.id);
      const ts = log.date || log.timestamp || '';
      const level = log.levelName || log.level || '';
      const msg = log.message || '';
      return `${ts}|${level}|${msg}`;
    },

    sortLogsAsc(logs) {
      return [...logs].sort((a, b) => {
        const dateA = new Date(a.date || a.timestamp || 0);
        const dateB = new Date(b.date || b.timestamp || 0);
        return dateA - dateB;
      });
    },

    // ==================== SCROLL ====================

    onScroll() {
      const container = this.$refs.logContainer;
      if (!container) return;

      const { scrollTop, scrollHeight, clientHeight } = container;

      // Check if at bottom
      this.isAtBottom = (scrollHeight - scrollTop - clientHeight) < this.bottomThreshold;

      // If user scrolls up, disable follow mode
      if (!this.isAtBottom) {
        this.followMode = false;
      }

      // Check if near top - trigger load older
      if (scrollTop < this.scrollThreshold && this.hasMoreOlder && !this.isLoadingOlder && !this.isLoadingInitial) {
        this.loadOlderLogs();
      }
    },

    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.logContainer;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    scrollToBottomAndFollow() {
      this.followMode = true;
      this.scrollToBottom();
    },

    preserveScrollPosition(addedHeight) {
      // After prepending content, adjust scroll to keep the same view
      const container = this.$refs.logContainer;
      if (container && addedHeight > 0) {
        container.scrollTop += addedHeight;
      }
    },

    // ==================== DATA LOADING ====================

    async loadInitialLogs() {
      if (this.isLoadingInitial || !this.executionId) return;

      this.isLoadingInitial = true;
      this.allLogs = [];
      this.seenIds = new Set();
      this.expandedContexts = new Set();
      this.newestLog = null;
      this.oldestLog = null;
      this.hasMoreOlder = true;

      try {
        const { default: JobRepository } = await import('@repository/JobRepository');
        const res = await JobRepository.executionLogs(this.executionId, {
          direction: 'desc',
          limit: 100
        });

        const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);

        if (items.length > 0) {
          const chronological = this.sortLogsAsc(items);

          this.oldestLog = chronological[0];
          this.newestLog = chronological[chronological.length - 1];

          for (const log of chronological) {
            const key = this.getLogKey(log);
            if (!this.seenIds.has(key)) {
              this.seenIds.add(key);
              this.allLogs.push(log);
            }
          }

          this.hasMoreOlder = items.length >= 100;
        } else {
          this.hasMoreOlder = false;
        }

        this.scrollToBottom();

      } catch (e) {
        console.error('[LogViewer] Initial load error:', e);
      } finally {
        this.isLoadingInitial = false;
      }

      // Start streaming
      this.startStreaming();
    },

    async loadOlderLogs() {
      if (this.isLoadingOlder || !this.executionId || !this.oldestLog || !this.hasMoreOlder) {
        return;
      }

      this.isLoadingOlder = true;

      // Save current scroll state
      const container = this.$refs.logContainer;
      const previousScrollHeight = container ? container.scrollHeight : 0;

      try {
        const { default: JobRepository } = await import('@repository/JobRepository');
        const res = await JobRepository.executionLogs(this.executionId, {
          direction: 'desc',
          limit: 100,
          beforeDate: this.oldestLog.date || this.oldestLog.timestamp,
          beforeId: this.oldestLog.id
        });

        const items = Array.isArray(res?.items) ? res.items : (Array.isArray(res) ? res : []);

        if (items.length > 0) {
          const chronological = this.sortLogsAsc(items);
          this.oldestLog = chronological[0];

          const newLogs = [];
          for (const log of chronological) {
            const key = this.getLogKey(log);
            if (!this.seenIds.has(key)) {
              this.seenIds.add(key);
              newLogs.push(log);
            }
          }

          if (newLogs.length > 0) {
            // Prepend older logs
            this.allLogs = [...newLogs, ...this.allLogs];

            // Preserve scroll position after DOM update
            this.$nextTick(() => {
              const newScrollHeight = container ? container.scrollHeight : 0;
              const addedHeight = newScrollHeight - previousScrollHeight;
              this.preserveScrollPosition(addedHeight);
            });
          }

          this.hasMoreOlder = items.length >= 100;
        } else {
          this.hasMoreOlder = false;
        }

      } catch (e) {
        console.error('[LogViewer] Error loading older logs:', e);
      } finally {
        this.isLoadingOlder = false;
      }
    },

    // ==================== SSE STREAMING ====================

    startStreaming() {
      this.stopStreaming();

      if (!this.executionId) return;

      let url = `/api/jobs/executions/${encodeURIComponent(this.executionId)}/logs/stream`;

      if (this.newestLog) {
        const params = new URLSearchParams();
        const afterDate = this.newestLog.date || this.newestLog.timestamp;
        const afterId = this.newestLog.id;

        if (afterDate) params.set('afterDate', afterDate);
        if (afterId) params.set('afterId', String(afterId));

        const qs = params.toString();
        if (qs) url += '?' + qs;
      }

      try {
        this.eventSource = new EventSource(url, { withCredentials: true });

        this.eventSource.addEventListener('log', (e) => {
          try {
            const log = JSON.parse(e.data);
            const key = this.getLogKey(log);

            if (!this.seenIds.has(key)) {
              this.seenIds.add(key);
              this.allLogs.push(log);
              this.newestLog = log;

              // Auto-scroll if in follow mode
              if (this.followMode) {
                this.scrollToBottom();
              }
            }
          } catch (err) {
            console.error('[LogViewer] SSE parse error:', err);
          }
        });

        this.eventSource.addEventListener('completed', () => {
          this.stopStreaming();
        });

        this.eventSource.onopen = () => {
          this.isStreaming = true;
        };

        this.eventSource.onerror = () => {
          this.stopStreaming();
        };

      } catch (e) {
        console.error('[LogViewer] SSE init error:', e);
      }
    },

    stopStreaming() {
      if (this.eventSource) {
        this.eventSource.close();
        this.eventSource = null;
      }
      this.isStreaming = false;
    },
  }
};
</script>

<style lang="scss" scoped>
.log-viewer {
  width: 100%;
  position: relative;
}

// xterm-like styling
.log-container {
  background: #111111;
  border-radius: 4px;
  height: 320px;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 10px;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
  font-size: 13px;
  line-height: 1.2;
  color: #DDDDDD;
}

.log-entry {
  margin-bottom: 2px;
}

.log-line {
  white-space: pre-wrap;
  word-break: break-word;
  min-height: 1.2em;
}

.log-timestamp {
  color: #7F7F7F;
}

.log-level {
  &.level-debug { color: #7F7F7F; }
  &.level-info { color: #5FFF87; }
  &.level-notice { color: #5FFFFF; }
  &.level-warning { color: #FFD75F; }
  &.level-error { color: #FF5F5F; }
  &.level-critical { color: #FF5F5F; font-weight: bold; }
  &.level-alert { color: #FF5F5F; font-weight: bold; }
  &.level-emergency { color: #FF5F5F; font-weight: bold; }
}

.log-message {
  color: #DDDDDD;
}

.context-toggle {
  background: transparent;
  border: 1px solid #444;
  border-radius: 3px;
  color: #888;
  font-size: 11px;
  padding: 1px 6px;
  margin-left: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  transition: all 0.15s ease;

  &:hover {
    background: #333;
    color: #aaa;
    border-color: #555;
  }

  .v-icon {
    color: inherit;
  }
}

.log-context-block {
  margin: 4px 0 8px 20px;
  padding: 8px 12px;
  background: #1a1a1a;
  border-left: 2px solid #444;
  border-radius: 0 4px 4px 0;
  overflow-x: auto;
}

.log-context-json {
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: #d4d4d4;
  white-space: pre;
  font-family: inherit;

  ::v-deep .json-key {
    color: #9cdcfe;
  }

  ::v-deep .json-string {
    color: #ce9178;
  }

  ::v-deep .json-number {
    color: #b5cea8;
  }

  ::v-deep .json-boolean {
    color: #569cd6;
  }

  ::v-deep .json-null {
    color: #569cd6;
  }
}

.empty-state {
  color: #7F7F7F;
  text-align: center;
  padding: 40px 0;
}

// Loading indicator (top right corner)
.loading-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 4px 8px;
  display: flex;
  align-items: center;
}

// Scroll to bottom button
.scroll-to-bottom-btn {
  position: absolute;
  bottom: 16px;
  right: 16px;
  opacity: 0.8;

  &:hover {
    opacity: 1;
  }
}

// Live indicator
.live-indicator {
  position: absolute;
  top: 8px;
  left: 8px;
  background: rgba(0, 0, 0, 0.6);
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  color: #5FFF87;
  display: flex;
  align-items: center;
  gap: 6px;
}

.live-dot {
  width: 6px;
  height: 6px;
  background: #5FFF87;
  border-radius: 50%;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.4; }
}

// Scrollbar styling
.log-container {
  &::-webkit-scrollbar {
    width: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #1a1a1a;
  }

  &::-webkit-scrollbar-thumb {
    background: #444;
    border-radius: 5px;

    &:hover {
      background: #555;
    }
  }
}
</style>
